import { G } from '@mobily/ts-belt';
import { Decimal } from 'decimal.js';
import { match } from 'ts-pattern';

import {
  EMPLOYEE_SAVINGS_CRDS_TAX_RATE,
  EMPLOYEE_SAVINGS_CSG_TAX_RATE,
} from '@Common/constants/employee_savings';
import { ValueSharingBonusFiscalRule } from '@Common/types/bo/b2b/campaigns';
import { TaxResidenceCode } from '@Common/types/bo/b2b/employees';
import { EmployeeSocialStatus } from '@Common/types/bo/b2b/employees_properties';
import { IntegerAmount, makeIntegerAmount } from '@Common/types/currency/currency';

export type PlanContributionTaxDetails = {
  user_type: EmployeeSocialStatus;
  tax_residence_code: TaxResidenceCode;
  tax_paid_by: 'company' | 'user';
  csg_tax_rate: number;
  crds_tax_rate: number;
  csg_tax_amount: IntegerAmount<2>;
  crds_tax_amount: IntegerAmount<2>;
  total_tax_amount: IntegerAmount<2>;
};

export type PlanContributionTaxes = {
  grossUserCurrencyAmount: IntegerAmount<2>;
  userCurrencyAmount: IntegerAmount<2>;
  employerFundRequestContributionTaxAmount: IntegerAmount<2>;
  employeePostContributionTaxAmount: IntegerAmount<2>;
  taxAmountDetails: PlanContributionTaxDetails;
};

/**
 * Splits one contribution's CSG/CRDS across several allocations.
 *
 * Converting each allocation's net to gross independently drifts by rounding
 * (3 × round(30.10 / 0.903) = 99.99, not 100.00). Instead the total tax is split pro rata to
 * each allocation's net share — `round(totalTax × net_i / totalNet)` for all but the last,
 * which takes the remainder — so the parts always sum back to the totals.
 */
export type ProportionalTaxAllocation = {
  /** Net amount of every allocation in the split, in declaration order. */
  allocationNetAmounts: IntegerAmount<2>[];
  /** Index of the allocation currently being computed within `allocationNetAmounts`. */
  currentIndex: number;
  /** Authoritative total gross for the whole split (the company's input). */
  totalGrossAmount: IntegerAmount<2>;
};

function zeroCompanyContributionTaxes({
  amount,
  socialStatus,
  taxResidenceCode,
}: {
  amount: IntegerAmount<2>;
  socialStatus: EmployeeSocialStatus;
  taxResidenceCode: TaxResidenceCode;
}): PlanContributionTaxes {
  return {
    grossUserCurrencyAmount: amount,
    userCurrencyAmount: amount,
    employerFundRequestContributionTaxAmount: makeIntegerAmount(0, 2),
    employeePostContributionTaxAmount: makeIntegerAmount(0, 2),
    taxAmountDetails: {
      user_type: socialStatus,
      tax_residence_code: taxResidenceCode,
      tax_paid_by: 'company',
      csg_tax_rate: 0,
      crds_tax_rate: 0,
      csg_tax_amount: makeIntegerAmount(0, 2),
      crds_tax_amount: makeIntegerAmount(0, 2),
      total_tax_amount: makeIntegerAmount(0, 2),
    },
  };
}

export function calculatePlanContributionTaxes({
  isNetAmountTaxContribution,
  amount,
  socialStatus,
  taxResidenceCode,
  valueSharingBonusFiscalRule,
  isExemptFromCsgCrds = false,
  proportionalTaxAllocation,
}: {
  isNetAmountTaxContribution: boolean;
  amount: IntegerAmount<2>;
  socialStatus: EmployeeSocialStatus;
  taxResidenceCode: TaxResidenceCode;
  valueSharingBonusFiscalRule: ValueSharingBonusFiscalRule | null;
  isExemptFromCsgCrds?: boolean;
  proportionalTaxAllocation?: ProportionalTaxAllocation;
}): PlanContributionTaxes {
  if (isExemptFromCsgCrds) {
    return zeroCompanyContributionTaxes({ amount, socialStatus, taxResidenceCode });
  }

  // Multi-allocation split: distribute the total tax pro rata instead of per-allocation.
  if (G.isNotNullable(proportionalTaxAllocation)) {
    return calculateProportionalPlanContributionTaxes({
      amount,
      socialStatus,
      taxResidenceCode,
      isExemptFromCsgCrds,
      ...proportionalTaxAllocation,
    });
  }

  return match({ taxResidenceCode, socialStatus })
    .with({ socialStatus: 'TS', taxResidenceCode: 'tax_resident' }, () => {
      // For Value Sharing Bonus, apply fiscal rule adjustments
      let taxRateMultiplier = 1; // Standard tax rate
      let deductionMultiplier = 1; // Standard (no additional deduction)

      if (G.isNotNullable(valueSharingBonusFiscalRule)) {
        const { taxRateMultiplier: vsbTaxRate, deductionMultiplier: vsbDeduction } = match(
          valueSharingBonusFiscalRule,
        )
          .with('exempt_from_csg_crds', () => ({
            taxRateMultiplier: 0, // No taxes
            deductionMultiplier: 1,
          }))
          .with('with_csg_crds_deduction', () => ({
            taxRateMultiplier: 1, // Standard tax rate
            deductionMultiplier: 0.9825, // 98.25% deduction
          }))
          .with('without_csg_crds_deduction', () => ({
            taxRateMultiplier: 1, // Standard tax rate (standard behavior)
            deductionMultiplier: 1, // No deduction (standard behavior)
          }))
          .exhaustive();

        taxRateMultiplier = vsbTaxRate;
        deductionMultiplier = vsbDeduction;
      }

      const totalTaxesRate =
        (EMPLOYEE_SAVINGS_CSG_TAX_RATE + EMPLOYEE_SAVINGS_CRDS_TAX_RATE) * taxRateMultiplier;
      if (isNetAmountTaxContribution) {
        // Apply deduction multiplier to tax rate for VSB with deduction
        // Formula: Gross = Net / (1 - (deductionMultiplier × totalTaxesRate))
        const grossAmount = new Decimal(amount)
          .div(1 - totalTaxesRate * deductionMultiplier)
          .round()
          .toNumber();

        const totalContributionTaxes = makeIntegerAmount(
          new Decimal(grossAmount).sub(amount).toNumber(),
          2,
        );

        return {
          grossUserCurrencyAmount: makeIntegerAmount(grossAmount, 2),
          userCurrencyAmount: amount,
          employerFundRequestContributionTaxAmount: totalContributionTaxes,
          employeePostContributionTaxAmount: makeIntegerAmount(0, 2),
          taxAmountDetails: {
            user_type: socialStatus,
            tax_residence_code: taxResidenceCode,
            tax_paid_by: 'company' as const,
            ...taxRatesAndAmounts({
              grossUserCurrencyAmount: makeIntegerAmount(grossAmount, 2),
              totalContributionTaxes,
              taxableBaseAmount: new Decimal(grossAmount).mul(deductionMultiplier),
            }),
          },
        };
      }
      const taxableBase = new Decimal(amount).mul(deductionMultiplier);
      const totalContributionTaxes = makeIntegerAmount(
        new Decimal(taxableBase).mul(totalTaxesRate).round().toNumber(),
        2,
      );
      const netAmount = amount - totalContributionTaxes;

      return {
        grossUserCurrencyAmount: amount,
        userCurrencyAmount: makeIntegerAmount(netAmount, 2),
        employerFundRequestContributionTaxAmount: totalContributionTaxes,
        employeePostContributionTaxAmount: makeIntegerAmount(0, 2),
        taxAmountDetails: {
          user_type: socialStatus,
          tax_residence_code: taxResidenceCode,
          tax_paid_by: 'company' as const,
          ...taxRatesAndAmounts({
            grossUserCurrencyAmount: amount,
            totalContributionTaxes,
            taxableBaseAmount: taxableBase,
          }),
        },
      };
    })
    .with(
      { socialStatus: 'TNS' },
      { taxResidenceCode: 'non_tax_resident' },
      { taxResidenceCode: 'resident_exempt' },
      () => ({
        grossUserCurrencyAmount: amount,
        userCurrencyAmount: amount,
        employerFundRequestContributionTaxAmount: makeIntegerAmount(0, 2),
        employeePostContributionTaxAmount: makeIntegerAmount(0, 2),
        taxAmountDetails: {
          user_type: socialStatus,
          tax_residence_code: taxResidenceCode,
          tax_paid_by: 'user' as const,
          csg_tax_rate: 0,
          crds_tax_rate: 0,
          csg_tax_amount: makeIntegerAmount(0, 2),
          crds_tax_amount: makeIntegerAmount(0, 2),
          total_tax_amount: makeIntegerAmount(0, 2),
        },
      }),
    )
    .exhaustive();
}

export function taxRatesAndAmounts({
  grossUserCurrencyAmount,
  totalContributionTaxes,
  taxableBaseAmount,
}: {
  grossUserCurrencyAmount: IntegerAmount<2>;
  totalContributionTaxes: IntegerAmount<2>;
  taxableBaseAmount?: Decimal.Value;
}): {
  csg_tax_rate: number;
  crds_tax_rate: number;
  csg_tax_amount: IntegerAmount<2>;
  crds_tax_amount: IntegerAmount<2>;
  total_tax_amount: IntegerAmount<2>;
} {
  if (totalContributionTaxes === 0) {
    return {
      csg_tax_rate: 0,
      crds_tax_rate: 0,
      csg_tax_amount: makeIntegerAmount(0, 2),
      crds_tax_amount: makeIntegerAmount(0, 2),
      total_tax_amount: makeIntegerAmount(0, 2),
    };
  }

  const taxableBase = new Decimal(taxableBaseAmount ?? grossUserCurrencyAmount);

  const csgTaxAmount = Decimal.min(
    taxableBase.mul(EMPLOYEE_SAVINGS_CSG_TAX_RATE).ceil(),
    totalContributionTaxes,
  ).toNumber();

  const crdsTaxAmount = new Decimal(totalContributionTaxes).sub(csgTaxAmount).toNumber();

  return {
    csg_tax_rate: EMPLOYEE_SAVINGS_CSG_TAX_RATE,
    crds_tax_rate: EMPLOYEE_SAVINGS_CRDS_TAX_RATE,
    csg_tax_amount: makeIntegerAmount(csgTaxAmount, 2),
    crds_tax_amount: makeIntegerAmount(crdsTaxAmount, 2),
    total_tax_amount: makeIntegerAmount(new Decimal(csgTaxAmount).add(crdsTaxAmount).toNumber(), 2),
  };
}

/**
 * Splits `totalAmount` pro rata to each allocation's net share; the last allocation takes the
 * remainder so the parts sum back to `totalAmount`.
 */
function allocateProportionalAmount({
  totalAmount,
  allocationNetAmounts,
  totalNet,
  index,
}: {
  totalAmount: number;
  allocationNetAmounts: IntegerAmount<2>[];
  totalNet: number;
  index: number;
}): number {
  const lastIndex = allocationNetAmounts.length - 1;

  // No net to distribute against: the last allocation takes everything.
  if (totalNet <= 0) {
    return index === lastIndex ? totalAmount : 0;
  }

  const proRata = (net: IntegerAmount<2>): number =>
    new Decimal(totalAmount).mul(net).div(totalNet).round().toNumber();

  if (index < lastIndex) {
    return proRata(allocationNetAmounts[index] ?? makeIntegerAmount(0, 2));
  }

  const allocatedToOthers = allocationNetAmounts
    .slice(0, lastIndex)
    .reduce((sum: number, net) => sum + proRata(net), 0);

  return totalAmount - allocatedToOthers;
}

/**
 * Computes a single allocation's share of the tax when one contribution is split across
 * several allocations.
 */
function calculateProportionalPlanContributionTaxes({
  amount,
  socialStatus,
  taxResidenceCode,
  isExemptFromCsgCrds,
  allocationNetAmounts,
  currentIndex,
  totalGrossAmount,
}: {
  amount: IntegerAmount<2>;
  socialStatus: EmployeeSocialStatus;
  taxResidenceCode: TaxResidenceCode;
  isExemptFromCsgCrds: boolean;
} & ProportionalTaxAllocation): PlanContributionTaxes {
  if (allocationNetAmounts.length === 0) {
    throw new Error('proportional tax allocation needs at least one net amount');
  }
  if (currentIndex < 0 || currentIndex >= allocationNetAmounts.length) {
    throw new Error('proportional tax allocation currentIndex is out of range');
  }

  const allocationNet = allocationNetAmounts[currentIndex];
  if (allocationNet === undefined) {
    throw new Error('proportional tax allocation currentIndex is out of range');
  }
  if (allocationNet !== amount) {
    throw new Error('proportional tax allocation amount must match the net at currentIndex');
  }

  const totalNet = allocationNetAmounts.reduce((sum: number, net) => sum + net, 0);

  // Only TS tax-residents are taxed (company-paid); everyone else has gross = net.
  const isCompanyPaidTaxable =
    !isExemptFromCsgCrds && socialStatus === 'TS' && taxResidenceCode === 'tax_resident';

  if (!isCompanyPaidTaxable) {
    return {
      grossUserCurrencyAmount: makeIntegerAmount(allocationNet, 2),
      userCurrencyAmount: makeIntegerAmount(allocationNet, 2),
      employerFundRequestContributionTaxAmount: makeIntegerAmount(0, 2),
      employeePostContributionTaxAmount: makeIntegerAmount(0, 2),
      taxAmountDetails: {
        user_type: socialStatus,
        tax_residence_code: taxResidenceCode,
        tax_paid_by: isExemptFromCsgCrds ? 'company' : 'user',
        csg_tax_rate: 0,
        crds_tax_rate: 0,
        csg_tax_amount: makeIntegerAmount(0, 2),
        crds_tax_amount: makeIntegerAmount(0, 2),
        total_tax_amount: makeIntegerAmount(0, 2),
      },
    };
  }

  if (totalGrossAmount < totalNet) {
    throw new Error('proportional tax allocation gross is below total net');
  }

  // Derive total CSG/CRDS once from the total gross, then split.
  const totalContributionTaxes = makeIntegerAmount(totalGrossAmount - totalNet, 2);
  const {
    csg_tax_amount: totalCsg,
    crds_tax_amount: totalCrds,
    csg_tax_rate: csgTaxRate,
    crds_tax_rate: crdsTaxRate,
  } = taxRatesAndAmounts({
    grossUserCurrencyAmount: totalGrossAmount,
    totalContributionTaxes,
  });

  const csgTaxAmount = allocateProportionalAmount({
    totalAmount: totalCsg,
    allocationNetAmounts,
    totalNet,
    index: currentIndex,
  });
  const crdsTaxAmount = allocateProportionalAmount({
    totalAmount: totalCrds,
    allocationNetAmounts,
    totalNet,
    index: currentIndex,
  });
  const allocationContributionTaxes = csgTaxAmount + crdsTaxAmount;

  return {
    grossUserCurrencyAmount: makeIntegerAmount(allocationNet + allocationContributionTaxes, 2),
    userCurrencyAmount: makeIntegerAmount(allocationNet, 2),
    employerFundRequestContributionTaxAmount: makeIntegerAmount(allocationContributionTaxes, 2),
    employeePostContributionTaxAmount: makeIntegerAmount(0, 2),
    taxAmountDetails: {
      user_type: socialStatus,
      tax_residence_code: taxResidenceCode,
      tax_paid_by: 'company',
      csg_tax_rate: csgTaxRate,
      crds_tax_rate: crdsTaxRate,
      csg_tax_amount: makeIntegerAmount(csgTaxAmount, 2),
      crds_tax_amount: makeIntegerAmount(crdsTaxAmount, 2),
      total_tax_amount: makeIntegerAmount(allocationContributionTaxes, 2),
    },
  };
}
